import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { PageContainer } from '@/components/ui/page-container'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { Callout } from '@/components/ui/callout'
import { useToast } from '@/components/ui/toast'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useFinishHoneybookSignIn } from '@/hooks/useHoneyBook'

/**
 * Where HoneyBook's sign-in sends the person back (docs/honeybook.spec.md,
 * "Connecting"). It hands the one-time code to the API as the signed-in person,
 * then moves on to the HoneyBook section of the venue the sign-in started from.
 *
 * The code works once, so it is sent once: a second send — React's development
 * double effect, or a reload of this page — would be refused by HoneyBook and
 * read as a failed sign-in.
 */
export function HoneyBookReturnPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const finish = useFinishHoneybookSignIn()
  const sent = useRef(false)
  const [error, setError] = useState<string | null>(null)

  const code = params.get('code')
  const state = params.get('state')
  const declined = params.get('error')

  useEffect(() => {
    if (sent.current || declined !== null || code === null || state === null) return
    sent.current = true
    finish.mutate(
      { code, state },
      {
        onSuccess: (done) => {
          if (done.connection === null) return
          toast(`Connected to ${done.connection.companyName}.`)
          void navigate(`/venues/${done.connection.venueId}/settings#honeybook`, { replace: true })
        },
        onError: (e) => setError(requestErrorMessage(e)),
      },
    )
  }, [code, state, declined, finish, navigate, toast])

  if (declined !== null) {
    return (
      <PageContainer width="wide">
        <Callout tone="attention" title="HoneyBook wasn’t connected">
          You stopped at HoneyBook, so nothing was connected.{' '}
          <Link to="/venues/all" className="font-medium text-ink underline underline-offset-2">
            Back to venues
          </Link>
        </Callout>
      </PageContainer>
    )
  }
  if (code === null || state === null) {
    return (
      <PageContainer width="wide">
        <ErrorState message="This page is only reached from HoneyBook’s sign-in. Start again from the venue’s settings." />
      </PageContainer>
    )
  }
  if (error !== null) {
    return (
      <PageContainer width="wide" className="space-y-4">
        <ErrorState message={error} />
        <p className="m-0 text-center text-sm">
          <Link to="/venues/all" className="font-medium text-ink underline underline-offset-2">
            Back to venues
          </Link>
        </p>
      </PageContainer>
    )
  }
  return (
    <PageContainer width="wide">
      <DetailSkeleton />
    </PageContainer>
  )
}
